import React, { useEffect, useState } from 'react';
import './styles/index.css';

function App() {
  const [navbarScrolled, setNavbarScrolled] = useState(false);
  const [scrollToTopVisible, setScrollToTopVisible] = useState(false);
  const [currentPeriod, setCurrentPeriod] = useState('yearly');
  const [activeTab, setActiveTab] = useState(0);

  // Navbar scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setNavbarScrolled(window.scrollY > 50);
      setScrollToTopVisible(window.pageYOffset > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        } else {
          // Remove visible class when out of view to re-animate on scroll
          if (entry.target.classList.contains('fade-in-bottom')) {
            entry.target.classList.remove('visible');
          }
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.fade-in, .feature-card, .stat-item, .benefits-card, .benefits-image-card, .content-item, .pricing-card, .about-content, .about-image, .fade-in-left, .fade-in-right, .fade-in-bottom');
    elements.forEach(el => observer.observe(el));

    return () => {
      elements.forEach(el => observer.unobserve(el));
    };
  }, []);

  // Animated counter for stats
  useEffect(() => {
    const animateCounter = (element) => {
      const target = parseFloat(element.getAttribute('data-target'));
      const duration = 2000;
      const increment = target / (duration / 16);
      let current = 0;

      const updateCounter = () => {
        current += increment;
        if (current < target) {
          if (target % 1 === 0) {
            element.textContent = Math.floor(current);
          } else {
            element.textContent = current.toFixed(1);
          }
          requestAnimationFrame(updateCounter);
        } else {
          if (target % 1 === 0) {
            element.textContent = target;
          } else {
            element.textContent = target.toFixed(1);
          }
        }
      };

      updateCounter();
    };

    const statObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !entry.target.classList.contains('counted')) {
          entry.target.classList.add('counted');
          const statNumber = entry.target.querySelector('.stat-number');
          if (statNumber) {
            animateCounter(statNumber);
          }
        }
      });
    }, { threshold: 0.5 });

    const statItems = document.querySelectorAll('.stat-item');
    statItems.forEach(el => statObserver.observe(el));

    return () => {
      statItems.forEach(el => statObserver.unobserve(el));
    };
  }, []);

  // Testimonials carousel
  const testimonials = [
    {
      text: "SHIVAM ITCS has completely transformed how we operate. The automation features alone have saved us countless hours every week. The ROI was visible within the first month.",
      author: "Sarah Mitchell",
      role: "CEO, TechCorp",
      initials: "SM"
    },
    {
      text: "The analytics dashboard gives us insights we never had before. It's like having a data scientist on our team 24/7. The AI-powered recommendations have been game-changing.",
      author: "James Davis",
      role: "CTO, InnovateLabs",
      initials: "JD"
    },
    {
      text: "Outstanding customer support and a platform that just works. We've tried many solutions, but SHIVAM ITCS stands out for its reliability and ease of use.",
      author: "Emily Chen",
      role: "Operations Director, GrowthCo",
      initials: "EC"
    },
    {
      text: "The integration capabilities are incredible. We've connected all our tools seamlessly, and the workflow automation has reduced our manual work by 70%.",
      author: "Michael Rodriguez",
      role: "VP Engineering, ScaleUp",
      initials: "MR"
    }
  ];

  // Duplicate testimonials for seamless marquee
  const marqueeTestimonials = [...testimonials, ...testimonials];


  // Smooth scroll for anchor links
  const handleSmoothScroll = (e, targetId) => {
    e.preventDefault();
    const target = document.querySelector(targetId);
    if (target) {
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <div className="App">
      {/* Background Elements */}
      <div className="grid-pattern"></div>
      <div className="gradient-orb orb-1"></div>
      <div className="gradient-orb orb-2"></div>
      <div className="gradient-orb orb-3"></div>

      {/* Navigation */}
      <nav id="navbar" className={navbarScrolled ? 'scrolled' : ''}>
        <div className="nav-container">
          <div className="logo">SHIVAM ITCS</div>
          <ul className="nav-links">
            <li><a href="#features" onClick={(e) => handleSmoothScroll(e, '#features')}>Features</a></li>
            <li><a href="#benefits" onClick={(e) => handleSmoothScroll(e, '#benefits')}>Benefits</a></li>
            <li><a href="#content" onClick={(e) => handleSmoothScroll(e, '#content')}>Content</a></li>
            <li><a href="#pricing" onClick={(e) => handleSmoothScroll(e, '#pricing')}>Pricing</a></li>
            <li><a href="#about" onClick={(e) => handleSmoothScroll(e, '#about')}>About</a></li>
          </ul>
          <button className="nav-cta">Get Started</button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <video 
          className="hero-video-bg"
          autoPlay
          loop
          muted
          playsInline
        >
          <source src="https://www.algolia.com/files/live/sites/www/files/homepage/ai-retrieval-hp-video-bottom-layer.mp4" type="video/mp4" />
        </video>
        <div className="hero-content">
          <div className="hero-badge">✨ New: AI-Powered Analytics</div>
          <h1>Build the Future<br />of Your Business</h1>
          <p>Transform your workflow with seamless integration, powerful automation, and intelligent insights.</p>
          <div className="hero-cta-group">
            <button className="btn-primary">Start Free Trial</button>
            <button className="btn-secondary">Watch Demo</button>
          </div>
          <img 
            src="https://framerusercontent.com/images/SDGdXNGfNOqKZyGuibtu3qIdGkw.svg?width=1024&height=327" 
            alt="Hero Decorative Image" 
            className="hero-image"
          />
        </div>
      </section>

      {/* Features Section */}
      <section className="features" id="features">
        <div className="section-header fade-in">
          <div className="section-badge">Features</div>
          <h2 className="section-title">Our Client Love to use Our Products</h2>
          <p className="section-description">Free Up Your Time to Focus on What Matters Most</p>
        </div>
        <div className="features-grid">
          <div className="feature-card fade-in">
            <img src="https://framerusercontent.com/images/whSMsukNUAYWMjBpmhe9a6JnD0.svg" alt="Intelligent Research Assistant Icon" className="feature-icon-top" />
            <h3>Intelligent Research Assistant</h3>
            <p>Feed the AI your topic and keywords, and watch it gather relevant information from across the web.</p>
            <img src="https://framerusercontent.com/images/NgHqg6PDC08O1bPOTUyhSTCG4.svg?scale-down-to=512" alt="Research Dashboard" className="feature-image-bottom fade-in-left" />
          </div>
          <div className="feature-card fade-in">
            <img src="https://framerusercontent.com/images/whSMsukNUAYWMjBpmhe9a6JnD0.svg" alt="AI-Powered Content Generation Icon" className="feature-icon-top" />
            <h3>AI-Powered Content Generation</h3>
            <p>Overcome writer's block and get fresh ideas with a single click. Awesome experience for the users.</p>
            <img src="https://framerusercontent.com/images/jAdKLWLKnqm6wt2Ys3pY6aGes1w.png?scale-down-to=512&width=1194&height=798" alt="Content Generation Interface" className="feature-image-bottom fade-in-left" />
          </div>
          <div className="feature-card feature-card-full fade-in">
            <img src="https://framerusercontent.com/images/xCBxbsbUvhDc7FL0iQCnSHUSok.svg" alt="Advanced Analytics Icon" className="feature-icon-top" />
            <h3>Advanced Analytics & Insights</h3>
            <p>Transform your data into actionable insights with powerful analytics tools that help you make informed decisions.</p>
            <img src="https://framerusercontent.com/images/hukL0KDLNb6oclGKkrqdkREkE.svg?scale-down-to=1024&width=1124&height=347" alt="Analytics Dashboard" className="feature-image-bottom fade-in-right" />
          </div>
          <div className="feature-card feature-card-cta fade-in">
            <div className="cta-content">
              <p className="cta-text">
                Stop writer's block and generate fresh<br />
                content ideas with our AI assistant. Take<br />
                your content to next level!
              </p>
            </div>
            <div className="cta-button-wrapper">
              <button className="cta-glow-button">
                Get A Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats" id="stats">
        <div className="stats-grid">
          <div className="stat-item fade-in">
            <div className="stat-number" data-target="10000">0</div>
            <div className="stat-label">Active Users</div>
          </div>
          <div className="stat-item fade-in">
            <div className="stat-number" data-target="99.9">0</div>
            <div className="stat-label">Uptime %</div>
          </div>
          <div className="stat-item fade-in">
            <div className="stat-number" data-target="500">0</div>
            <div className="stat-label">Integrations</div>
          </div>
          <div className="stat-item fade-in">
            <div className="stat-number" data-target="24">0</div>
            <div className="stat-label">Support Hours</div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about" id="about">
        <div className="about-wrapper">
          <div className="about-header fade-in">
           
            <h2 className="section-badge">About Us</h2>
           
          </div>
          
          <div className="about-content-main fade-in">
            <p className="about-main-text">
              We're a passionate team of innovators obsessed with using AI to streamline content creation. We believe AI can revolutionise the writing industry by making high-quality content generation faster and more accessible
            </p>
          </div>

          <div className="about-button-wrapper fade-in">
            <button className="about-cta-button">
              More About Us
              <span className="about-arrow">↗</span>
            </button>
          </div>

          <div className="about-features-grid fade-in">
            <div className="about-feature-card about-feature-card-premium" data-feature="content-ideas">
              <div className="about-feature-icon" style={{ color: '#FF8A00' }}>✦</div>
              <span className="about-feature-text">Content Ideas</span>
            </div>
            <div className="about-feature-card about-feature-card-premium" data-feature="writers-assist">
              <div className="about-feature-icon" style={{ color: '#8B5CF6' }}>✦</div>
              <span className="about-feature-text">Writer's Assist</span>
            </div>
            <div className="about-feature-card about-feature-card-premium" data-feature="quality-control">
              <div className="about-feature-icon" style={{ color: '#10B981' }}>✦</div>
              <span className="about-feature-text">Quality Control</span>
            </div>
            <div className="about-feature-card" data-feature="faster-workflow">
              <div className="about-feature-icon" style={{ color: '#3B82F6' }}>✦</div>
              <span className="about-feature-text">Faster Workflow</span>
            </div>
            <div className="about-feature-card" data-feature="increased-output">
              <div className="about-feature-icon" style={{ color: '#EF4444' }}>✦</div>
              <span className="about-feature-text">Increased Output</span>
            </div>
            <div className="about-feature-card" data-feature="super-brain">
              <div className="about-feature-icon" style={{ color: '#FBBF24' }}>✦</div>
              <span className="about-feature-text">Super-Brain</span>
            </div>
            <div className="about-feature-card about-feature-card-centered" data-feature="style-variations">
              <div className="about-feature-icon" style={{ color: '#8B5CF6' }}>✦</div>
              <span className="about-feature-text">Style Variations</span>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits" id="benefits">
        <div className="benefits-container">
          <div className="section-header fade-in">
            <div className="section-badge">Benefits</div>
            <h2 className="section-title">Your Benefits</h2>
            <p className="section-description">Everything you need to succeed, all in one powerful platform. Connect with the tools you love and streamline your workflow.</p>
          </div>
          <div className="benefits-card fade-in">
            <h3 style={{ fontSize: '2rem',  marginBottom: '1rem', textAlign: 'center' }}>Innovative Essential Platforms</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '2rem', textAlign: 'center' }}>
              Witness our groundbreaking e-commerce platform that seamlessly connects buyers and sellers worldwide.
            </p>
            <div className="benefits-icons-wrapper">
              <div className="icon-row icon-row-left">
                <div className="benefit-icon-container icon-blue">📊</div>
                <div className="benefit-icon-container icon-purple">🔗</div>
                <div className="benefit-icon-container icon-pink">⚡</div>
                <div className="benefit-icon-container icon-green">🔐</div>
                <div className="benefit-icon-container icon-orange">📱</div>
                <div className="benefit-icon-container icon-cyan">🤖</div>
                <div className="benefit-icon-container icon-red">📈</div>
                <div className="benefit-icon-container icon-indigo">🌐</div>
                <div className="benefit-icon-container icon-blue">💼</div>
                <div className="benefit-icon-container icon-purple">🎯</div>
                <div className="benefit-icon-container icon-pink">🚀</div>
                <div className="benefit-icon-container icon-green">⚙️</div>
                <div className="benefit-icon-container icon-blue">📊</div>
                <div className="benefit-icon-container icon-purple">🔗</div>
                <div className="benefit-icon-container icon-pink">⚡</div>
                <div className="benefit-icon-container icon-green">🔐</div>
                <div className="benefit-icon-container icon-orange">📱</div>
                <div className="benefit-icon-container icon-cyan">🤖</div>
                <div className="benefit-icon-container icon-red">📈</div>
                <div className="benefit-icon-container icon-indigo">🌐</div>
                <div className="benefit-icon-container icon-blue">💼</div>
                <div className="benefit-icon-container icon-purple">🎯</div>
                <div className="benefit-icon-container icon-pink">🚀</div>
                <div className="benefit-icon-container icon-green">⚙️</div>
              </div>
              <div className="icon-row icon-row-right">
                <div className="benefit-icon-container icon-orange">🔔</div>
                <div className="benefit-icon-container icon-cyan">📧</div>
                <div className="benefit-icon-container icon-red">💡</div>
                <div className="benefit-icon-container icon-indigo">🎨</div>
                <div className="benefit-icon-container icon-blue">📦</div>
                <div className="benefit-icon-container icon-purple">🔍</div>
                <div className="benefit-icon-container icon-pink">💳</div>
                <div className="benefit-icon-container icon-green">📝</div>
                <div className="benefit-icon-container icon-orange">🎬</div>
                <div className="benefit-icon-container icon-cyan">🏆</div>
                <div className="benefit-icon-container icon-red">⭐</div>
                <div className="benefit-icon-container icon-indigo">🎪</div>
                <div className="benefit-icon-container icon-orange">🔔</div>
                <div className="benefit-icon-container icon-cyan">📧</div>
                <div className="benefit-icon-container icon-red">💡</div>
                <div className="benefit-icon-container icon-indigo">🎨</div>
                <div className="benefit-icon-container icon-blue">📦</div>
                <div className="benefit-icon-container icon-purple">🔍</div>
                <div className="benefit-icon-container icon-pink">💳</div>
                <div className="benefit-icon-container icon-green">📝</div>
                <div className="benefit-icon-container icon-orange">🎬</div>
                <div className="benefit-icon-container icon-cyan">🏆</div>
                <div className="benefit-icon-container icon-red">⭐</div>
                <div className="benefit-icon-container icon-indigo">🎪</div>
              </div>
            </div>
          </div>
          <div className="benefits-image-card fade-in">
            <h3 style={{ fontSize: '2rem',  marginBottom: '1rem', textAlign: 'center', background: 'linear-gradient(135deg, #F4F7FF 0%, #6366F1 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Effortless Personalization</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '2rem', textAlign: 'center' }}>
              AI tailors the user experience to each visitor. Imagine a website that remembers preferences, recommends relevant products.
            </p>
            <div className="benefits-svg-wrapper">
              <img src="https://framerusercontent.com/images/W9tWjZrDnTEvZNkQBAKlCA2ehcw.svg" alt="Effortless Personalization UI" className="benefits-svg-image fade-in-left" />
            </div>
          </div>
          <div className="benefits-image-card fade-in">
            <h3 style={{ fontSize: '2rem',  marginBottom: '1rem', textAlign: 'center', background: 'linear-gradient(135deg, #F4F7FF 0%, #6366F1 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Continual Improvement</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '2rem', textAlign: 'center' }}>
              Our platform evolves with your needs. Continuous updates and enhancements ensure you always have access to the latest features and improvements.
            </p>
            <div className="benefits-svg-wrapper">
              <img src="https://framerusercontent.com/images/RBsfOiShcP77HbQ74m8laT315g.png?scale-down-to=512" alt="Continual Improvement" className="benefits-svg-image fade-in-bottom" />
            </div>
          </div>
          <div className="benefits-image-card fade-in">
            <h3 style={{ fontSize: '2rem',  marginBottom: '1rem', textAlign: 'center', background: 'linear-gradient(135deg, #F4F7FF 0%, #6366F1 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Boost Efficiency & Productivity</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '2rem', textAlign: 'center' }}>
              Streamline your workflows and automate repetitive tasks. Our intelligent tools help you accomplish more in less time, boosting your team's productivity.
            </p>
            <div className="benefits-svg-wrapper">
              <img src="https://framerusercontent.com/images/3QmB6tExvbsoRGTMhC1qPppA.png?scale-down-to=512" alt="Boost Efficiency & Productivity" className="benefits-svg-image fade-in-right" />
            </div>
          </div>
          <div className="benefits-image-card fade-in">
            <h3 style={{ fontSize: '2rem',  marginBottom: '1rem', textAlign: 'center', background: 'linear-gradient(135deg, #F4F7FF 0%, #6366F1 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Actionable Insights</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '2rem', textAlign: 'center' }}>
              Make data-driven decisions with real-time analytics and intelligent reporting. Get actionable insights that help you understand your business better and drive growth.
            </p>
            <div className="benefits-svg-wrapper">
              <img src="https://framerusercontent.com/images/5Sg5LVeutnjYbOtqTjZ3ltz9L8.png?scale-down-to=512" alt="Actionable Insights" className="benefits-svg-image fade-in-right" />
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="content" id="content">
        <div className="section-header fade-in">
          <div className="section-badge">Build for you</div>
          <h2 className="section-title">What we've Built for you</h2>
          <p className="section-description">Comprehensive solutions designed to meet all your business needs.</p>
        </div>
        
        {/* Tab Carousel */}
        <div className="build-tabs-container fade-in">
          <div className="build-tabs-content">
            <div className={`build-tab-panel ${activeTab === 0 ? 'active' : ''}`}>
              <img 
                src="https://framerusercontent.com/images/3QmB6tExvbsoRGTMhC1qPppA.png?scale-down-to=1000" 
                alt="Build for you" 
                className="build-tab-image"
              />
            </div>
            <div className={`build-tab-panel ${activeTab === 1 ? 'active' : ''}`}>
              <img 
                src="https://framerusercontent.com/images/5Sg5LVeutnjYbOtqTjZ3ltz9L8.png?scale-down-to=1000" 
                alt="Build for you" 
                className="build-tab-image"
              />
            </div>
            <div className={`build-tab-panel ${activeTab === 2 ? 'active' : ''}`}>
              <img 
                src="https://framerusercontent.com/images/3QmB6tExvbsoRGTMhC1qPppA.png?scale-down-to=1000" 
                alt="Build for you" 
                className="build-tab-image"
              />
            </div>
          </div>
          
          <div className="build-tabs-nav">
            <button 
              className={`build-tab-indicator ${activeTab === 0 ? 'active' : ''}`}
              onClick={() => setActiveTab(0)}
              aria-label="Tab 1"
            ></button>
            <button 
              className={`build-tab-indicator ${activeTab === 1 ? 'active' : ''}`}
              onClick={() => setActiveTab(1)}
              aria-label="Tab 2"
            ></button>
            <button 
              className={`build-tab-indicator ${activeTab === 2 ? 'active' : ''}`}
              onClick={() => setActiveTab(2)}
              aria-label="Tab 3"
            ></button>
          </div>
        </div>
      </section>

      {/* Indicator Section */}
      <section className="indicator" id="indicator">
        <div className="indicator-wrapper">
          <div className="indicator-header fade-in">
            <div className="indicator-header-line"></div>
            <div className="indicator-badge">Indicator</div>
            <div className="indicator-header-line"></div>
          </div>
          
          <h2 className="indicator-title fade-in">We Are Proud To Have A Great Indicator.</h2>

          {/* Review Card */}
          <div className="indicator-review-card fade-in">
            <div className="review-profiles">
              <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=faces" alt="Reviewer 1" className="review-profile review-profile-1" />
              <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=faces" alt="Reviewer 2" className="review-profile review-profile-2" />
              <img src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=faces" alt="Reviewer 3" className="review-profile review-profile-3" />
            </div>
            <div className="review-content">
              <div className="review-rating">
                <span className="rating-number">4.9/5</span>
                <div className="rating-stars">
                  <span className="star">★</span>
                  <span className="star">★</span>
                  <span className="star">★</span>
                  <span className="star">★</span>
                  <span className="star">★</span>
                </div>
              </div>
              <p className="review-text">Based on 24 reviews on Clutch</p>
            </div>
          </div>

          {/* Statistics Grid */}
          <div className="indicator-stats-grid fade-in">
            <div className="indicator-stat-item">
              <div className="indicator-stat-number indicator-stat-blue">11.5M +</div>
              <div className="indicator-stat-label">Clients Revenue</div>
            </div>
            <div className="indicator-stat-item">
              <div className="indicator-stat-number indicator-stat-pink">11.5M +</div>
              <div className="indicator-stat-label">Clients Revenue</div>
            </div>
            <div className="indicator-stat-item">
              <div className="indicator-stat-number indicator-stat-blue">21.9M +</div>
              <div className="indicator-stat-label">Transactions recorded</div>
            </div>
            <div className="indicator-stat-item">
              <div className="indicator-stat-number indicator-stat-pink">300 +</div>
              <div className="indicator-stat-label">Project Done</div>
            </div>
          </div>

          {/* Trusted By Section */}
          <div className="indicator-trusted fade-in">
            <p className="trusted-text">Trusted by 500+ Brands & Companies</p>
            <div className="trusted-logos">
              <img src="https://framerusercontent.com/images/LeKLhKQujLwTElyuS9zDmz4979E.png?width=147&height=33" alt="Company Logo" className="trusted-logo" />
              <img src="https://framerusercontent.com/images/wCMFBGRPwdGSTAfJcBcMsCULHpw.png?width=144&height=25" alt="Company Logo" className="trusted-logo" />
              <img src="https://framerusercontent.com/images/TEY7rgbXRQQKvogsD8Kr1CVdjM.png?width=146&height=27" alt="Company Logo" className="trusted-logo" />
              <img src="https://framerusercontent.com/images/zwINoBx34OUqqSIbk4zs12wRPwA.png?width=141&height=23" alt="Company Logo" className="trusted-logo" />
              <img src="https://framerusercontent.com/images/5FuAQ7QHvlGTQW79NOOnRbziuw.png?width=98&height=29" alt="Company Logo" className="trusted-logo" />
              <img src="https://framerusercontent.com/images/G46FPYdmXh7jVTkEiOkK5rF2vNI.png?width=115&height=27" alt="Company Logo" className="trusted-logo" />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials" id="testimonials">
        <div className="section-header fade-in">
          <div className="section-badge">Testimonials</div>
          <h2 className="section-title">What Our Customers Say</h2>
          <p className="section-description">Don't just take our word for it - hear from our satisfied customers.</p>
        </div>
        <div className="testimonials-marquee-container">
          <div className="testimonials-marquee">
            {marqueeTestimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, fontStyle: 'italic', marginBottom: '1.5rem', fontSize: '1.125rem' }}>
                  "{testimonial.text}"
                </p>
                <div className="testimonial-author">
                  <div className="testimonial-avatar">{testimonial.initials}</div>
                  <div>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '1.125rem' }}>{testimonial.author}</div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing" id="pricing">
        <div className="section-header fade-in">
          <div className="subscription-badge">Subscription</div>
          <h2 className="section-title">Choose your plan</h2>
          <p className="section-description">All plans come with a 60-day money-back guarantee.</p>
        </div>
        <div className="pricing-toggle-wrapper fade-in">
          <div className="pricing-toggle" id="pricingToggle">
            <button 
              className={`pricing-toggle-option ${currentPeriod === 'monthly' ? 'active' : ''}`}
              onClick={() => setCurrentPeriod('monthly')}
              data-period="monthly"
            >
              Monthly
            </button>
            <button 
              className={`pricing-toggle-option ${currentPeriod === 'yearly' ? 'active' : ''}`}
              onClick={() => setCurrentPeriod('yearly')}
              data-period="yearly"
            >
              Yearly
            </button>
          </div>
        </div>
        <div className="pricing-grid">
          <div className="pricing-card fade-in" data-plan="basic">
            <div className="pricing-card-icon">
              <img src="https://framerusercontent.com/images/T3hcOdVwQGUi1zL8N4mQzlxLxo.svg" alt="Basic Plan Icon" />
            </div>
            <h3>Basic</h3>
            <p className="pricing-card-description">Harnessing the power of artificial intelligence to revolutionize industries</p>
            <ul className="pricing-features">
              <li>Access 20+ Features</li>
              <li>Basic analytics</li>
              <li>Email support</li>
              <li>Community access</li>
              <li>Standard integrations</li>
            </ul>
            <button className="btn-secondary" style={{ width: '100%', marginTop: 'auto' }}>Get Started</button>
          </div>
          <div className="pricing-card fade-in" data-plan="standard">
            <div className="pricing-card-icon">
              <img src="https://framerusercontent.com/images/rnaRQjHGThLwiGGopZJLpPxv13w.svg" alt="Standard Plan Icon" />
            </div>
            <h3>Standard</h3>
            <p className="pricing-card-description">Harnessing the power of artificial intelligence to revolutionize industries</p>
            <ul className="pricing-features">
              <li>Access 80+ Enterprise</li>
              <li>Advanced analytics</li>
              <li>Priority support</li>
              <li>Custom integrations</li>
              <li>AI-powered insights</li>
            
            </ul>
            <button className="btn-primary" style={{ width: '100%', marginTop: 'auto' }}>Get Started</button>
          </div>
          <div className="pricing-card fade-in" data-plan="enterprise">
            <div className="pricing-card-icon">
              <img src="https://framerusercontent.com/images/rnaRQjHGThLwiGGopZJLpPxv13w.svg" alt="Enterprise Plan Icon" />
            </div>
            <h3>Enterprise</h3>
            <p className="pricing-card-description">Harnessing the power of artificial intelligence to revolutionize industries</p>
            <ul className="pricing-features">
              <li>Unlimited Features</li>
              <li>Full platform access</li>
              <li>24/7 dedicated support</li>
              <li>Custom integrations</li>
              <li>Advanced AI insights</li>
            
            </ul>
            <button className="btn-secondary" style={{ width: '100%', marginTop: 'auto' }}>Contact Sales</button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-container fade-in">
          <h2>Ready to Transform Your Business?</h2>
          <p>Join thousands of companies already using SHIVAM ITCS to streamline their operations and accelerate growth.</p>
          <button className="btn-primary">Start Your Free Trial</button>
        </div>
      </section>

      {/* Footer */}
      <footer>
        <div className="footer-content">
          <div className="footer-section footer-section-company">
            <h4>SHIVAM ITCS</h4>
            <p style={{ color: 'var(--text-secondary)' }}>Building the future of business automation, one feature at a time. We are committed to delivering innovative solutions that transform how businesses operate and grow. Our cutting-edge technology empowers organizations to achieve unprecedented efficiency and success.</p>
          </div>
          <div className="footer-section">
            <h4>Menu</h4>
            <ul>
              <li><a href="#features" onClick={(e) => handleSmoothScroll(e, '#features')}>Features</a></li>
              <li><a href="#benefits" onClick={(e) => handleSmoothScroll(e, '#benefits')}>Benefits</a></li>
              <li><a href="#content" onClick={(e) => handleSmoothScroll(e, '#content')}>Content</a></li>
              <li><a href="#pricing" onClick={(e) => handleSmoothScroll(e, '#pricing')}>Pricing</a></li>
              <li><a href="#about" onClick={(e) => handleSmoothScroll(e, '#about')}>About</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>Contact</h4>
            <ul className="footer-contact">
              <li>
                <span style={{ color: 'var(--text-secondary)' }}>Email:</span>{' '}
                <a href="mailto:Contact@shivamitcs.in" style={{ color: 'var(--text-primary)' }}>Contact@shivamitcs.in</a>
              </li>
              <li>
                <span style={{ color: 'var(--text-secondary)' }}>Phone:</span>{' '}
                <a href="tel:9413833633" style={{ color: 'var(--text-primary)' }}>9413833633</a>
              </li>
              <li>
                <span style={{ color: 'var(--text-secondary)' }}>Web:</span>{' '}
                <a href="https://shivamitcs.in" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-primary)' }}>Shivamitcs.in</a>
              </li>
              <li>
                <span style={{ color: 'var(--text-secondary)' }}>Address:</span>{' '}
                <span style={{ color: 'var(--text-primary)' }}>Lodha Complex, Nathdwara</span>
              </li>
            </ul>
            <p className="footer-copyright">
              All right and reserved by{' '}
              <a href="https://shivamitcs.in" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-primary)' }}>
                shivamitcs.in
              </a>
            </p>
          </div>
        </div>
        <div className="footer-bottom">
          <h2 className="footer-brand fade-in-bottom">SHIVAM</h2>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      <button 
        className={`scroll-to-top ${scrollToTopVisible ? 'visible' : ''}`}
        onClick={scrollToTop}
        aria-label="Scroll to top"
      >
      </button>
    </div>
  );
}

export default App;

